import * as React from "react";
import { useState, useEffect } from "react";
import {
  Title,
  Datagrid,
  TextField,
  DateField,
  useDataProvider,
  useNotify,
  ReferenceField,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Divider,
  alpha,
  MenuItem,
  Select,
} from "@mui/material";
import {
  BarChart,
  PieChart,
  ChartsTooltip,
  ChartsAxis,
  PieArcLabel,
  BarElement,
} from "@mui/x-charts";
import {
  LocalShipping as ShippedIcon,
  Payment as PaidIcon,
  PendingActions as PendingIcon,
  BuildCircle as ProcessingIcon,
  CheckCircle as DeliveredIcon,
  Cancel as CancelledIcon,
  Person as BuyerIcon,
  Store as SellerIcon,
  Category as TypeIcon,
  Timeline as TimelineIcon,
  AttachMoney as RevenueIcon,
} from "@mui/icons-material";
import { ExportListAction } from "./UsersReport";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField as MuiTextField } from "@mui/material";
import { OrderList } from "../Orders";
import ServiceList from "../Services";
import { OfferList } from "../Offers";
import InputProductList from "../InputProducts";

// Format SDG currency with K/M abbreviations
export const formatSDG = (amount) => {
  if (!amount) return "0 SDG";

  amount = Number(amount);
  if (isNaN(amount)) return "0 SDG";

  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M SDG`;
  }
  if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}K SDG`;
  }
  return `${amount.toLocaleString()} SDG`;
};

// Status icon component with modern styling
const StatusIcon = ({ status }) => {
  const iconProps = {
    fontSize: "small",
    sx: {
      mr: 1,
      borderRadius: "50%",
      padding: "4px",
      backgroundColor: alpha("#fff", 0.2),
    },
  };
  switch (status) {
    case "Pending":
      return <PendingIcon color="warning" {...iconProps} />;
    case "Processing":
      return <ProcessingIcon color="info" {...iconProps} />;
    case "Paid":
      return <PaidIcon color="success" {...iconProps} />;
    case "Shipped":
      return <ShippedIcon color="secondary" {...iconProps} />;
    case "Delivered":
      return <DeliveredIcon color="success" {...iconProps} />;
    case "Cancelled":
      return <CancelledIcon color="error" {...iconProps} />;
    default:
      return <PendingIcon color="action" {...iconProps} />;
  }
};

// Modern Status Field component
const StatusField = ({ record }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: "12px",
      backgroundColor: alpha("#fff", 0.1),
      width: "fit-content",
    }}
  >
    <StatusIcon status={record?.status} />
    <Typography variant="body2" fontWeight="500">
      {record?.status}
    </Typography>
  </Box>
);

// Modern Summary Card Component
export const SummaryCard = ({ title, value, icon: Icon, secondaryValue }) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: "0 4px 20px 0 rgba(0,0,0,0.12)",
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        transition: "all 0.3s ease",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.light,
          0.1
        )} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
        backdropFilter: "blur(8px)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 25px 0 rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "24px !important",
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: "12px",
            width: 60,
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            color: theme.palette.primary.contrastText,
            boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
          }}
        >
          <Icon fontSize="medium" />
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            gutterBottom
            sx={{ opacity: 0.8 }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            component="div"
            fontWeight="800"
            sx={{
              background: `linear-gradient(135deg, ${
                theme.palette.text.primary
              } 0%, ${alpha(theme.palette.text.primary, 0.8)} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {value}
          </Typography>
          {secondaryValue && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ opacity: 0.7 }}
            >
              {secondaryValue}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const SafePieChart = ({ data, title, height = 300 }) => {
  const theme = useTheme();

  // Define color palette to cycle through for unknown items
  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.primary.light,
    theme.palette.secondary.light,
    theme.palette.info.light,
  ];

  // Predefined color mappings
  const colorMap = [
    theme.palette.secondary.main,
    theme.palette.primary.main,
    theme.palette.info.main,

    theme.palette.war,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.secondary.main,
    theme.palette.success.dark,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.error.main,

    // Arabic service types
    theme.palette.primary.light,
    theme.palette.secondary.light,
    theme.palette.warning.light,
  ];

  if (!data || data.length === 0) {
    return (
      <Card
        sx={{
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: "blur(8px)",
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {title}
          </Typography>
          <Alert severity="info">No data available for this chart</Alert>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item, index) => {
    const category = item.status || item.type || item.name || "unknown";
    return {
      id: category,
      label: category,
      value: parseInt(item.count || item.totalRevenue || 0),
      // Use mapped color if exists, otherwise use palette based on index
      color: colorMap[category] || colorPalette[index % colorPalette.length],
    };
  });

  return (
    <Card
      sx={{
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(8px)",
        height: "100%",
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box sx={{ height }}>
          <PieChart
            series={[
              {
                data: chartData,
                innerRadius: 40,
                outerRadius: 100,
                paddingAngle: 2,
                cornerRadius: 6,
                highlightScope: { faded: "global", highlighted: "item" },
                faded: {
                  innerRadius: 30,
                  additionalRadius: -30,
                  color: "gray",
                },
                arcLabel: (item) => `${item.label} (${item.value})`,
                arcLabelMinAngle: 20,
              },
            ]}
            colors={chartData.map((item) => item.color)}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
                labelStyle: {
                  fontSize: 12,
                  fill: theme.palette.text.primary,
                },
              },
            }}
          >
            <ChartsTooltip />
            <PieArcLabel />
          </PieChart>
        </Box>
      </CardContent>
    </Card>
  );
};

// Modern Bar Chart Component
export const SafeBarChart = ({
  data,
  title,
  xKey = "month",
  yKey = "count",
  height = 300,
}) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Card
        sx={{
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: "blur(8px)",
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {title}
          </Typography>
          <Alert severity="info">No data available for this chart</Alert>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item) => ({
    [xKey]: `${item.month || item.type} ${item.year || ""}`.trim(),
    [yKey]: parseInt(item[yKey] || 0),
  }));

  return (
    <Card
      sx={{
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(8px)",
        height: "100%",
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box sx={{ height }}>
          <BarChart
            dataset={chartData}
            xAxis={[
              {
                scaleType: "band",
                dataKey: xKey,
                label: xKey === "month" ? "Month" : "Type",
                labelStyle: {
                  fontSize: 12,
                  fill: theme.palette.text.primary,
                },
                tickLabelStyle: {
                  fontSize: 10,
                  fill: theme.palette.text.secondary,
                },
              },
            ]}
            series={[
              {
                dataKey: yKey,
                label: yKey === "count" ? "Count" : "Revenue",
                color: theme.palette.primary.main,
                highlightScope: {
                  highlighted: "item",
                  faded: "global",
                },
              },
            ]}
            yAxis={[
              {
                label: yKey === "count" ? "Number of Orders" : "Revenue (SDG)",
                labelStyle: {
                  fontSize: 12,
                  fill: theme.palette.text.primary,
                },
              },
            ]}
            colors={[theme.palette.primary.main]}
            grid={{ vertical: true }}
          >
            <ChartsAxis />
            <ChartsTooltip
              formatter={(value) =>
                yKey === "count" ? value : formatSDG(value)
              }
            />
            <BarElement
              style={{
                rx: 4,
                ry: 4,
              }}
            />
          </BarChart>
        </Box>
      </CardContent>
    </Card>
  );
};

// Modern Top Performers List Component

const TypesReports = ({ reportType }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const [gender, setGender] = useState("");
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedtypes] = useState("");
  const [startDate, setStartDate] = useState(""); // YYYY‑MM‑DD
  const [endDate, setEndDate] = useState("");
  const genders = ["male", "female"];
  const dynamicTitle =
    reportType === "offers"
      ? "Offers"
      : reportType === "services"
      ? "Services"
      : "Inputs";
  const token = JSON.parse(localStorage.getItem("token"));

  useEffect(() => {
    const fetchMetaData = async () => {
      setSelectedtypes("");
      setLoading(true);
      const stateResponse = await fetch(
        `${process.env.REACT_APP_URL}/admin/state`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      const statesData = await stateResponse.json();
      setStates(statesData);
      const typesResponse = await fetch(
        `${process.env.REACT_APP_URL}/admin/${
          reportType === "offers"
            ? "sub_category"
            : reportType === "services"
            ? "servicetypes"
            : "inputs"
        }`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      const typesData = await typesResponse.json();
      setTypes(typesData["data"]);
    };
    setLoading(false);
    fetchMetaData();
  }, [reportType]);
  useEffect(() => {
    const fetchReportData = async () => {
      const params = new URLSearchParams();
      try {
        if (selectedState)
          params.append("filter[]", `state||$eq||${selectedState}`);
        if (selectedType)
          params.append(
            "filter[]",
            `${
              reportType === "offers"
                ? "subCategory_id"
                : reportType === "services"
                ? "service_id"
                : "category_id"
            }||$eq||${selectedType}`
          );
        if (gender) params.append("filter[]", `gender||$eq||${gender}`);
        if (startDate)
          params.append("filter[]", `created_at_gte||$cont||${startDate}`);
        if (endDate)
          params.append("filter[]", `created_at_lte||$cont||${endDate}`);
        const url =
          `${process.env.REACT_APP_URL}/admin/${
            reportType === "offers"
              ? "offers"
              : reportType === "services"
              ? "services"
              : "input-products"
          }` + (params.toString() ? `?${params.toString()}` : "");
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setReportData(data);
      } catch (err) {
        console.error("Error fetching report data:", err);
        setError(err);
        notify("Error loading order report data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchReportData();
  }, [selectedState, selectedType, gender, reportType, startDate, endDate]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        Failed to load order report data. Please try again later.
        <Box mt={1} fontFamily="monospace">
          {error.message}
        </Box>
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        No order report data available.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title={dynamicTitle + " Reports"} />
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="800"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          {dynamicTitle + " Analytics Dashboard"}
        </Typography>
      </Box>

      {/* Summary Cards */}

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label={dynamicTitle + " List"} />
          </Tabs>
        </Paper>
      </Box>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">State</Typography>
              <Select
                fullWidth
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">
                {" "}
                {dynamicTitle + " Type"}
              </Typography>
              <Select
                fullWidth
                value={selectedType}
                onChange={(e) => setSelectedtypes(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {types.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">Gender</Typography>
              <Select
                fullWidth
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">Any</MenuItem>
                {genders.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">From</Typography>
              <DatePicker
                value={startDate || null}
                onChange={(newDate) => {
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setStartDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">To</Typography>
              <DatePicker
                value={endDate || null}
                onChange={(newDate) => {
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setEndDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SafePieChart
                data={reportData["typeDistribution"]}
                title="Type Distribution"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SafePieChart
                data={reportData["userDemographics"]["gender"]}
                title="Gender Distribution"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SafePieChart
                data={reportData["userDemographics"]["topLocations"]}
                title="Location Distribution"
              />
            </Grid>
          </Grid>
        </>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          {reportType === "offers" ? (
            <OfferList resource="offers" />
          ) : reportType === "services" ? (
            <ServiceList resource="services" />
          ) : (
            <InputProductList resource="input-products" />
          )}
        </Grid>
      )}
    </Box>
  );
};

export default TypesReports;
